import { createContext, useContext, useMemo, type ReactNode } from "react";
import { useLoaderData, type LoaderFunctionArgs } from "react-router";

import { findSeedPage, findSeedSection } from "@/data/sections";

import { fetchPageData, type PageData, type SectionDoc } from "./content";

export type { PageData };

/**
 * One page's own content: its record and its sections, in the order an editor
 * put them.
 *
 * Every route the app renders itself loads this for its own slug. A component
 * anywhere inside that route reads it with `usePageData`, and the admin panel's
 * preview supplies a draft copy through `PageDataProvider` instead, which is how
 * the preview shows unsaved edits with the real components.
 */

const OverrideContext = createContext<PageData | null>(null);

export function PageDataProvider({ value, children }: { value: PageData; children: ReactNode }) {
  return <OverrideContext.Provider value={value}>{children}</OverrideContext.Provider>;
}

export const isPageData = (value: unknown): value is PageData =>
  Boolean(value && typeof value === "object" && "slug" in value && "sections" in value && "seeded" in value);

/** The SSR host passes the API's content version through the router context. */
export const contentVersionFrom = (context: unknown) => (context as { contentVersion?: string } | undefined)?.contentVersion;

/** A route loader for one system page. */
export function pageLoader(slug: string) {
  return ({ request, context }: LoaderFunctionArgs) =>
    fetchPageData(slug, { signal: request.signal, version: contentVersionFrom(context) });
}

export function usePageData(): PageData | null {
  const override = useContext(OverrideContext);
  const loaded = useLoaderData();
  return override ?? (isPageData(loaded) ? loaded : null);
}

/** A page's committed sections, for a database nobody has seeded. */
export function seedSectionsFor(slug: string): SectionDoc[] {
  return (findSeedPage(slug)?.sections ?? []) as unknown as SectionDoc[];
}

/**
 * The sections to render, in order.
 *
 * Saved sections where any exist, without the hidden ones; the committed page
 * where none do. See `PageData.seeded` for why those two states must not be
 * confused: collapsing them once made hiding a section in the panel do nothing,
 * because the page fell back to its committed copy and looked exactly as before.
 */
export function orderedSections(data: PageData | null): SectionDoc[] {
  if (!data) return [];
  if (!data.seeded) return seedSectionsFor(data.slug);
  return data.sections
    .filter((section) => !section.hidden)
    .slice()
    .sort((a, b) => a.order - b.order);
}

/**
 * One section on a page by key.
 *
 * The saved section; the committed one when nothing is saved for the page at
 * all; null when it has been hidden or removed. A removed section is never
 * brought back from the committed copy once the page has saved sections.
 */
export function sectionByKey(data: PageData | null, key: string): SectionDoc | null {
  if (!data?.seeded) return (findSeedSection(key) as unknown as SectionDoc | null) ?? null;
  const found = data.sections.find((section) => section.key === key);
  return found && !found.hidden ? found : null;
}

export function useOrderedSections(): SectionDoc[] {
  const data = usePageData();
  return useMemo(() => orderedSections(data), [data]);
}

/** Used where one section reads another, as About's registration panel reads the governance cards. */
export function usePageSection(key: string): SectionDoc | null {
  const data = usePageData();
  return useMemo(() => sectionByKey(data, key), [data, key]);
}
